import type { ReactNode } from 'react';
import { Boton, FilaAcciones, Insignia, MensajeError, TextoAyuda } from '../../ui/base';
import { Modal } from '../../ui/Modal';

/** Las piezas que comparten las secciones de Configuración: encabezado, botones de fila y la confirmación. */

export function EncabezadoSeccion({
  titulo,
  ayuda,
  agregar,
  onAgregar,
  e2eAgregar,
}: {
  titulo: string;
  ayuda: ReactNode;
  agregar?: string;
  onAgregar?: () => void;
  e2eAgregar?: string;
}) {
  return (
    <div className="mb-4 flex flex-wrap items-start justify-between gap-4 sm:flex-nowrap">
      <div className="min-w-0 flex-1">
        <h2 className="text-titulo font-extrabold">{titulo}</h2>
        <TextoAyuda className="mb-0">{ayuda}</TextoAyuda>
      </div>
      {agregar && onAgregar && (
        <Boton className="shrink-0" onClick={onAgregar} data-e2e={e2eAgregar}>
          + {agregar}
        </Boton>
      )}
    </div>
  );
}

export function EstadoActivo({ activo, femenino = false }: { activo: boolean; femenino?: boolean }) {
  const letra = femenino ? 'a' : 'o';
  return activo ? <Insignia tono="exito">Activ{letra}</Insignia> : <Insignia tono="neutro">Inactiv{letra}</Insignia>;
}

/** Editar · Desactivar/Activar · Eliminar. Sin `onEstado` ni `onEliminar`, ese botón no aparece. */
export function BotonesFila({
  nombre,
  activo,
  onEditar,
  onEstado,
  onEliminar,
  ocupado,
}: {
  nombre: string;
  activo: boolean;
  onEditar: () => void;
  onEstado?: () => void;
  onEliminar?: () => void;
  ocupado: boolean;
}) {
  return (
    <div className="flex items-center justify-end gap-2 whitespace-nowrap">
      <Boton variante="contorno" onClick={onEditar} disabled={ocupado} data-e2e={`editar-${nombre}`}>
        Editar
      </Boton>
      {onEstado && (
        <Boton variante="contorno" onClick={onEstado} disabled={ocupado} data-e2e={`estado-${nombre}`}>
          {activo ? 'Desactivar' : 'Activar'}
        </Boton>
      )}
      {onEliminar && (
        <Boton
          variante="contornoPeligro"
          onClick={onEliminar}
          disabled={ocupado}
          data-e2e={`eliminar-${nombre}`}
        >
          Eliminar
        </Boton>
      )}
    </div>
  );
}

/** Confirma un borrado que no se deshace, dice qué pasa y ofrece desactivar en su lugar. */
export function ConfirmarEliminar({
  titulo,
  puntos,
  onEliminar,
  onDesactivar,
  onCerrar,
  ocupado,
  error,
}: {
  /** null cierra la ventana. */
  titulo: string | null;
  puntos: string[];
  onEliminar: () => void;
  onDesactivar?: () => void;
  onCerrar: () => void;
  ocupado: boolean;
  error: string;
}) {
  return (
    <Modal abierto={titulo !== null} titulo={titulo ?? ''} onCerrar={onCerrar}>
      <div className="space-y-4" data-e2e="confirmar-eliminar">
        <ul className="list-disc space-y-1 pl-5">
          {puntos.map((p) => (
            <li key={p}>{p}</li>
          ))}
          <li>No se puede deshacer.{onDesactivar ? ' Si solo quieres dejar de usarlo un tiempo, desactívalo.' : ''}</li>
        </ul>
        <MensajeError>{error}</MensajeError>
        <FilaAcciones>
          <Boton variante="contorno" onClick={onCerrar}>
            Cancelar
          </Boton>
          {onDesactivar && (
            <Boton variante="contorno" onClick={onDesactivar} disabled={ocupado}>
              Desactivar
            </Boton>
          )}
          <Boton variante="peligro" onClick={onEliminar} disabled={ocupado} data-e2e="confirmar-eliminar-si">
            Eliminar
          </Boton>
        </FilaAcciones>
      </div>
    </Modal>
  );
}
